import type { GridBaseProps } from "@repo/ui";
import type { MouseEvent } from "react";

import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { SortTypeEnum, splitWordsOnUpperCase } from "@repo/helpers";
import { Button, ButtonColor, ButtonSize, Column, GridPreLoaded, OriginalTimezoneDateColumn } from "@repo/ui";
import { useMemo, useState } from "react";

import type { RepDiscipline } from "../../../models";
import type { RepAdditionalTraining } from "../../../models/repDiscipline";

import { useTabCountQuery } from "../../../api/repApi";
import { useRepDisciplineApi, useRepDisciplineQueries } from "../../../api/repDisciplineApi";
import { useRepSegmentsApi } from "../../../api/repSegmentsApi";
import { DeleteAlertDialog } from "../../../components/deleteAlertDialog";
import { useRepContext, useRepDisciplineContext } from "../../../contexts/editableItemContext";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import { RepDisciplineReason, RepDisciplineType } from "../../../models/repDiscipline";
import style from "./style.module.scss";

interface Props extends GridBaseProps<RepDiscipline> {
	repId: number;
}
const COLUMNS = [
	new Column("Type", "type")
		.withFormat((r: RepDisciplineType) => splitWordsOnUpperCase(RepDisciplineType[r]))
		.withMinWidth(75),
	new OriginalTimezoneDateColumn("Start Date", "startDate").dateOnly().withSortBy(SortTypeEnum.descending),
	new OriginalTimezoneDateColumn("End Date", "endDate").dateOnly(),
	new Column("Reason", "reason").withFormat((r: RepDisciplineReason) => RepDisciplineReason[r]),
	new Column("Reason Details", "reasonText"),
	new Column("Assigned Training(s)", "assignedTrainings").withFormat(
		(n: RepAdditionalTraining[]) => n.map((t) => t.trainingName).join(",") ?? "N/A",
	),
] as Column<RepDiscipline>[];

export default function RepDisciplineGridBasic(props: Props) {
	const { repId } = useRepContext();
	const { item, clearItem } = useRepDisciplineContext();
	const { useRepDisciplineQuery } = useRepDisciplineQueries();
	const { data, loaded, clearQuery } = useRepDisciplineQuery();
	const { deleteSuspension } = useRepDisciplineApi();
	const { useRepSegmentsQuery, getRepSegment } = useRepSegmentsApi(repId);
	const { data: repSegments, loading: repSegmentsLoading } = useRepSegmentsQuery();
	const [settings] = useSettingsQuery();
	const { clearQuery: clearTabCount } = useTabCountQuery(repId);
	const { multiStateEnabled } = settings;

	const [deleteDisciplineId, setDeleteDisciplineId] = useState<number | null>(null);

	const columns = useMemo(() => {
		const allColumns = [
			new Column<RepDiscipline>("", "repDisciplineId").withFullObject().withFormat(renderDeleteButton),
			...COLUMNS,
		];

		if (multiStateEnabled && !repSegmentsLoading)
			allColumns.push(
				new Column<RepDiscipline>("Onboarding State(s)", "segmentId").withFormat((id: number[]) => {
					if (!id) return "All";

					if (id.length === repSegments.length) return "ALL";

					return (
						repSegments
							.filter((s) => id.includes(s.id))
							.map((s) => s.name)
							.join(" | ") ?? ""
					);
				}),
			);
		return allColumns;
	}, [getRepSegment, multiStateEnabled]);

	async function confirmDelete() {
		if (deleteDisciplineId) {
			if (await deleteSuspension(deleteDisciplineId)) {
				if (item?.repDisciplineId === deleteDisciplineId) clearItem();
				await clearQuery();
				await clearTabCount();
			}
			setDeleteDisciplineId(null);
		}
	}

	function renderDeleteButton(repDiscipline: RepDiscipline) {
		const { repDisciplineId, startDate, endDate } = repDiscipline;

		function deleteButtonClick(e: MouseEvent) {
			e.stopPropagation();
			e.preventDefault();
			setDeleteDisciplineId(repDisciplineId);
		}

		const currentDate = new Date();
		const disabled = startDate < currentDate && (!endDate || currentDate < endDate);

		return (
			<div className={style.editButtons}>
				<Button
					color={ButtonColor.Green}
					onClick={deleteButtonClick}
					size={ButtonSize.medium}
					disabled={disabled}
				>
					<FontAwesomeIcon icon={faTimes} size="lg" />
					Delete
				</Button>
			</div>
		);
	}

	return (
		<>
			<GridPreLoaded
				{...props}
				pageSize={3}
				title="Rep Disciplines"
				columns={columns}
				values={data?.data ?? []}
				loading={!loaded}
				getData={clearQuery}
			/>
			<DeleteAlertDialog
				isOpen={!!deleteDisciplineId}
				onClose={() => setDeleteDisciplineId(null)}
				onConfirm={confirmDelete}
				title="Delete Discipline"
				description="Are you sure you want to delete this discipline?"
			/>
		</>
	);
}
